import { PRIMARY_LOCALE } from "routes/healthForms/types/common";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, FormControl, InputLabel, MenuItem, Select, TextField, Typography } from "@material-ui/core";
import { Add } from "@material-ui/icons";
import { CardContainer } from "routes/createContent/components/styles";
import { useGetTemplates } from "routes/createContent/api/useGetTemplates";
import { useDeleteTemplate } from "routes/createContent/api/useDeleteTemplate";
import { ConfirmDeleteDialog, DeletedChip, ShowDeletedToggle } from "routes/createContent/components/CatalogueList";
import { CatalogueDeploymentRow } from "routes/createContent/components/CatalogueDeploymentRow";
import { LibraryItem } from "../../components/LibraryItem/LibraryItem";
import { Link, useHistory } from "react-router-dom";
import { CataloguePath } from "routes/createContent/paths";
import { GroupIcon } from "shared/GroupIcon";
import { Loading } from "shared";
import { humanize } from "./constants";
import { useViewState } from "utils/useViewState";

export const Templates: React.VFC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [{ search, group, sort, deleted }, setView] = useViewState({
    search: "",
    group: "all",
    sort: "asc",
    deleted: false,
  });
  const { data: templates, isLoading, error } = useGetTemplates(deleted);
  const deletion = useDeleteTemplate();
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);

  if (isLoading) {
    return <Loading />;
  }

  if (error) {
    return <p>{t("errors.generic")}</p>;
  }

  const named = (templates ?? []).map(template => ({
    template,
    name: String(template.name[PRIMARY_LOCALE.code] || template.id),
  }));
  const groups = Array.from(new Set(named.map(({ template }) => template.group ?? "other"))).sort();
  const shown = named
    .filter(({ name }) => name.toLowerCase().includes(search.trim().toLowerCase()))
    .filter(({ template }) => group === "all" || (template.group ?? "other") === group)
    .sort((a, b) => (sort === "asc" ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name)));

  return (
    <div>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <h1 style={{ margin: 0 }}>Templates</h1>
        <Button
          color="primary"
          variant="contained"
          startIcon={<Add />}
          component={Link}
          to={CataloguePath.Templates_New}
        >
          New template
        </Button>
      </Box>

      <Box display="flex" alignItems="center" mb={2} style={{ gap: 16 }}>
        <TextField
          variant="outlined"
          size="small"
          label="Search"
          value={search}
          onChange={e => setView({ search: e.target.value })}
        />
        <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
          <InputLabel>Group</InputLabel>
          <Select label="Group" value={group} onChange={e => setView({ group: e.target.value as string })}>
            <MenuItem value="all">All</MenuItem>
            {groups.map(g => (
              <MenuItem key={g} value={g}>
                {humanize(g)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" style={{ minWidth: 120 }}>
          <InputLabel>Sort</InputLabel>
          <Select label="Sort" value={sort} onChange={e => setView({ sort: e.target.value as string })}>
            <MenuItem value="asc">A–Z</MenuItem>
            <MenuItem value="desc">Z–A</MenuItem>
          </Select>
        </FormControl>
        <ShowDeletedToggle value={deleted} onChange={value => setView({ deleted: value })} />
        <Box flexGrow={1} />
        <Typography variant="h6">{shown.length} results</Typography>
      </Box>

      {deletion.isError && (
        <Box color="error.main" my={2}>
          {deletion.error instanceof Error ? deletion.error.message : String(deletion.error)}
        </Box>
      )}

      <CardContainer>
        {shown.map(({ template, name }) => {
          return (
            <LibraryItem
              key={template.id}
              author={undefined}
              checked={false}
              id={template.id}
              image={<GroupIcon group={template.group} />}
              title={name}
              icon={template.deleted_at ? <DeletedChip /> : undefined}
              bottomContent={
                <CatalogueDeploymentRow kind="template" name={template.key} rollout={template.rollout} compact />
              }
              onClick={() => {
                if (!template.deleted_at) history.push(`${CataloguePath.Templates}/${template.id}`);
              }}
              onDelete={
                template.deleted_at
                  ? undefined
                  : () => {
                      deletion.reset();
                      setConfirmDelete(template.id);
                    }
              }
            />
          );
        })}
      </CardContainer>

      <ConfirmDeleteDialog
        noun="template"
        open={confirmDelete != null}
        loading={deletion.isLoading}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => {
          if (confirmDelete == null) return;
          deletion.mutate(confirmDelete, { onSuccess: () => setConfirmDelete(null) });
        }}
      />
    </div>
  );
};
